import type { DataTableColumn, DataTableSortState } from '@atrvd/saas-ui'
import { compareText, type ManualAccountTableRow } from './manualBanks.utils'

/**
 * Client-side sorting for the manual-bank tables.
 *
 * The tables hold their sort in component state (not in column prefs or the
 * URL), so leaving the page and coming back restores the default sort.
 */

/** Signed comparison helper: `cmp` for ascending, negated for descending. */
export function signed(direction: 'asc' | 'desc', cmp: number): number {
  return direction === 'asc' ? cmp : -cmp
}

/** Comparator for one text field of an account row. */
export function byText(pick: (row: ManualAccountTableRow) => string) {
  return (a: ManualAccountTableRow, b: ManualAccountTableRow, direction: 'asc' | 'desc') =>
    signed(direction, compareText(pick(a), pick(b)))
}

/**
 * Facility order, and within one facility the account number ascending — the
 * detail table's default sort. The tie-break does not flip with the facility
 * direction: inside a facility the numbers always read upwards.
 */
export function byFacilityThenNumber(
  a: ManualAccountTableRow,
  b: ManualAccountTableRow,
  direction: 'asc' | 'desc',
): number {
  const facility = compareText(a.facilityName, b.facilityName)
  if (facility !== 0) return signed(direction, facility)
  return compareText(a.account.account_number ?? '', b.account.account_number ?? '')
}

/**
 * Accounts in Cash Position first when ascending, as the prototype sorts the
 * column; within each group by name, which does not flip with the direction.
 */
export function byCashPosition(
  a: ManualAccountTableRow,
  b: ManualAccountTableRow,
  direction: 'asc' | 'desc',
): number {
  if (a.inCashPosition !== b.inCashPosition) return signed(direction, a.inCashPosition ? -1 : 1)
  return compareText(a.account.name, b.account.name)
}

/**
 * `rows` in the order of `sort`, using the sorted column's `sortFn` — what
 * DataTable's client mode does, but over EVERY row, so a paged table can sort
 * first and slice after (DataTable only ever sees the current page). Rows keep
 * their order when nothing is sorted or the column has no `sortFn`.
 *
 * @param rows - All rows of the table
 * @param columns - The table's columns; the sorted one supplies the comparator
 * @param sort - The table's sort state (the first entry is the one applied)
 */
export function sortRows<T>(
  rows: readonly T[],
  columns: readonly DataTableColumn<T>[],
  sort: readonly DataTableSortState[],
): T[] {
  const active = sort[0]
  const direction = active?.direction
  const sortFn = active ? columns.find((column) => column.key === active.columnKey)?.sortFn : undefined
  if (!direction || !sortFn) return [...rows]
  return [...rows].sort((a, b) => sortFn(a, b, direction))
}
